import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { captureEvent, captureError } from '../utils/posthogUtils';
import CreateYesNoModal from '../components/CreateYesNoModal';
import BulkUploadYesNoModal from '../components/BulkUploadYesNoModal';
import getApiUrl from '../utils/api';
import Pagination from '../components/Pagination';
import './YesNo.css';
import DashboardLayout from '../layouts/DashboardLayout';

const SHORT_WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const UPCOMING_LOOKAHEAD_DAYS = 7;

const normalizeWeekdayToken = (token) => {
  if (token == null) {
    return null;
  }

  if (typeof token === 'number' && Number.isInteger(token)) {
    if (token >= 0 && token <= 6) {
      return token;
    }
    if (token >= 1 && token <= 7) {
      return token % 7;
    }
  }

  const trimmed = token.toString().trim().toLowerCase();
  if (!trimmed) {
    return null;
  }

  const numeric = parseInt(trimmed, 10);
  if (!Number.isNaN(numeric)) {
    if (numeric >= 0 && numeric <= 6) {
      return numeric;
    }
    if (numeric >= 1 && numeric <= 7) {
      return numeric % 7;
    }
  }

  const lookup = {
    sun: 0,
    sunday: 0,
    mon: 1,
    monday: 1,
    tue: 2,
    tues: 2,
    tuesday: 2,
    wed: 3,
    weds: 3,
    wednesday: 3,
    thu: 4,
    thur: 4,
    thurs: 4,
    thursday: 4,
    fri: 5,
    friday: 5,
    sat: 6,
    saturday: 6,
  };

  return Object.prototype.hasOwnProperty.call(lookup, trimmed) ? lookup[trimmed] : null;
};

const formatCustomDaysLabel = (value) => {
  if (value == null) {
    return '-';
  }

  const tokens = Array.isArray(value)
    ? value
    : value
      .toString()
      .split(/[,|\s]+/)
      .filter(Boolean);

  const normalized = Array.from(
    new Set(
      tokens
        .map(normalizeWeekdayToken)
        .filter((index) => index !== null)
    )
  ).sort((a, b) => a - b);

  if (!normalized.length) {
    return '-';
  }

  return normalized
    .map((index) => SHORT_WEEKDAY_LABELS[index] || index.toString())
    .join(', ');
};

const YesNo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('planning');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [user, setUser] = useState({
    name: '',
    designation: '',
    department: '',
  });
  const [showDepartment, setShowDepartment] = useState(true);
  const [showLocation, setShowLocation] = useState(true);
  const [departments, setDepartments] = useState([]);

  // Scoped Data States
  const [planningState, setPlanningState] = useState({
    items: [],
    loading: false,
    error: null,
    page: 1,
    limit: 10,
    total: 0,
    search: '',
    filterFreq: 'All'
  });

  const [executionState, setExecutionState] = useState({
    items: [],
    loading: false,
    error: null,
    page: 1,
    limit: 10,
    total: 0,
    search: '', // Committed search term
    localSearch: '', // UI state for input
    filterFreq: 'All'
  });

  const [pastState, setPastState] = useState({
    items: [],
    loading: false,
    error: null,
    page: 1,
    limit: 10,
    total: 0,
    search: '', // Committed search term
    localSearch: '', // UI state for input
    filterFreq: 'All',
    month: 'All'
  });

  const [editingPlannedId, setEditingPlannedId] = useState(null);
  const [editingPlannedTimes, setEditingPlannedTimes] = useState({ from: '', to: '' });
  const [editingExecutionId, setEditingExecutionId] = useState(null);
  const [editingExecutionData, setEditingExecutionData] = useState({
    actualSubject: '',
    actualFromTime: '',
    actualToTime: '',
    plannedQty: '',
    actualQty: '',
    marks: '',
    yesStatus: false,
    noStatus: false,
  });
  const [todayDate] = useState(new Date()); // Today's date for filtering
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarksData, setRemarksData] = useState({
    remarks: '',
    yesNoId: null,
    yesStatus: false,
    noStatus: false,
  });
  const [viewRemarksData, setViewRemarksData] = useState(null);
  const [isTodayExpanded, setIsTodayExpanded] = useState(true);
  const [isPastExpanded, setIsPastExpanded] = useState(false);

  const posthog = usePostHog();

  const toLocalDateKey = (dateInput) => {
    if (!dateInput) return null;
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateKeyToDate = (dateKey) => {
    if (!dateKey || typeof dateKey !== 'string') return null;
    const parts = dateKey.split('-');
    if (parts.length !== 3) return null;
    const [year, month, day] = parts.map((value) => parseInt(value, 10));
    if ([year, month, day].some(Number.isNaN)) return null;
    return new Date(year, month - 1, day);
  };

  // Get today's date formatted (YYYY-MM-DD)
  const getTodayDateString = () => {
    return toLocalDateKey(new Date());
  };

  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const localKey = typeof isoDate === 'string' && isoDate.includes('T')
      ? toLocalDateKey(isoDate)
      : isoDate;
    if (!localKey) return '';
    const parts = localKey.split('-');
    if (parts.length !== 3) return isoDate;
    const [year, month, day] = parts;
    return `${day}-${month}-${year}`;
  };

  const handleToggleKeyPress = (event, toggleFn) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleFn((prev) => !prev);
    }
  };

  const todayStr = getTodayDateString();

  const formatDayDisplay = (item) => {
    if (!item) {
      return '-';
    }
    const frequency = (item.frequency || '').trim();
    if (frequency !== 'Custom') {
      return '-';
    }

    const dateKey = toLocalDateKey(item.occurrenceDate);
    if (!dateKey) {
      return formatCustomDaysLabel(item.customDays);
    }

    if (dateKey === todayStr) {
      return 'Today';
    }

    const dateObj = dateKeyToDate(dateKey);
    if (!dateObj) {
      return '-';
    }

    return dateObj.toLocaleDateString('en-IN', { weekday: 'short' });
  };

  const getOccurrenceDisplayDate = (item) => {
    const dateKey = toLocalDateKey(item?.occurrenceDate);
    if (!dateKey) {
      return '-';
    }

    if (dateKey === todayStr) {
      return 'Today';
    }

    return formatDateForDisplay(dateKey);
  };

  const getRecurrenceGroupingKey = (item) => {
    if (!item) {
      return null;
    }

    if (item.recurrenceGroupId) {
      return item.recurrenceGroupId;
    }

    const isRecurring = Boolean(item.isRecurring) || ['Daily', 'Alternate', 'Weekly', 'Monthly', 'Quarterly', 'Yearly', 'Custom'].includes((item.frequency || '').trim());
    if (!isRecurring) {
      return null;
    }

    const slugParts = [item.name, item.department, item.location, item.frequency]
      .map((part) => (part || '').toString().trim().toLowerCase())
      .filter(Boolean);

    if (!slugParts.length) {
      return null;
    }

    return `fallback::${slugParts.join('|')}`;
  };

  const pickPreferredOccurrence = (existingItem, candidateItem) => {
    if (!existingItem) {
      return candidateItem;
    }
    if (!candidateItem) {
      return existingItem;
    }

    const existingDateKey = toLocalDateKey(existingItem.occurrenceDate);
    const candidateDateKey = toLocalDateKey(candidateItem.occurrenceDate);

    if (existingDateKey && candidateDateKey) {
      if (candidateDateKey < existingDateKey) {
        return candidateItem;
      }
      if (candidateDateKey > existingDateKey) {
        return existingItem;
      }
      const existingId = existingItem.id ?? Number.MAX_SAFE_INTEGER;
      const candidateId = candidateItem.id ?? Number.MAX_SAFE_INTEGER;
      return candidateId < existingId ? candidateItem : existingItem;
    }

    if (!existingDateKey && candidateDateKey) {
      return candidateItem;
    }

    if (existingDateKey && !candidateDateKey) {
      return existingItem;
    }

    const existingId = existingItem.id ?? Number.MAX_SAFE_INTEGER;
    const candidateId = candidateItem.id ?? Number.MAX_SAFE_INTEGER;
    return candidateId < existingId ? candidateItem : existingItem;
  };

  const dedupeByRecurrenceGroup = (items) => {
    const indexByGroup = new Map();
    const result = [];

    items.forEach((item) => {
      if (!item) {
        return;
      }

      const groupKey = getRecurrenceGroupingKey(item);

      if (!groupKey) {
        result.push(item);
        return;
      }

      const existingIndex = indexByGroup.get(groupKey);
      if (existingIndex === undefined) {
        indexByGroup.set(groupKey, result.length);
        result.push(item);
        return;
      }

      const preferred = pickPreferredOccurrence(result[existingIndex], item);
      result[existingIndex] = preferred;
    });

    return result;
  };

  const dedupeByRecurrenceGroupAndDate = (items) => {
    const indexByComposite = new Map();
    const result = [];

    items.forEach((item) => {
      if (!item) {
        return;
      }

      const groupKey = getRecurrenceGroupingKey(item);
      if (!groupKey) {
        result.push(item);
        return;
      }

      const dateKey = toLocalDateKey(item.occurrenceDate) || 'no-date';
      const compositeKey = `${groupKey}::${dateKey}`;

      if (!indexByComposite.has(compositeKey)) {
        indexByComposite.set(compositeKey, result.length);
        result.push(item);
        return;
      }

      const existingIndex = indexByComposite.get(compositeKey);
      const preferred = pickPreferredOccurrence(result[existingIndex], item);
      result[existingIndex] = preferred;
    });

    return result;
  };

  // Filter Daily Checklist items for today (or specific date for recurring items)
  const filterForToday = (items) => {
    return items.filter((item) => {
      if (!item.occurrenceDate) {
        return !item.isRecurring; // Show non-recurring items even without occurrence date
      }
      const itemDateStr = toLocalDateKey(item.occurrenceDate);
      return itemDateStr === todayStr;
    });
  };

  const applyUserData = (rawUser) => {
    if (!rawUser || typeof rawUser !== 'object') {
      return false;
    }

    const firstName = (
      rawUser.FirstName ||
      rawUser.first_name ||
      rawUser.firstName ||
      ''
    ).toString();
    const lastName = (
      rawUser.LastName ||
      rawUser.last_name ||
      rawUser.lastName ||
      ''
    ).toString();
    const fullName = `${firstName} ${lastName}`
      .replace(/\s+/g, ' ')
      .trim();

    const department = (
      rawUser.DepartmentName ||
      rawUser.department_name ||
      rawUser.departmentName ||
      rawUser.department ||
      rawUser.Department?.name ||
      rawUser.departmentTitle ||
      ''
    )
      .toString()
      .trim();

    const designation = (
      rawUser.DesignationName ||
      rawUser.designation ||
      rawUser.designationName ||
      rawUser.Designation?.name ||
      rawUser.role ||
      ''
    )
      .toString()
      .trim();

    setUser({
      name: fullName || rawUser.name || rawUser.fullName || '',
      designation: designation || '',
      department: department || '',
    });
    return true;
  };

  useEffect(() => {
    let isMounted = true;

    const loadUser = async () => {
      try {
        const localData = localStorage.getItem('user');
        if (localData) {
          const parsed = JSON.parse(localData);
          if (applyUserData(parsed)) {
            return;
          }
        }
      } catch (err) {
        console.error('Error parsing stored user data:', err);
      }

      try {
        const response = await fetch(getApiUrl('api/auth/me'), {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user profile: ${response.status}`);
        }

        const data = await response.json();
        const resolvedUser = data?.user || data;
        if (isMounted && resolvedUser) {
          try {
            const existing = localStorage.getItem('user');
            if (!existing) {
              localStorage.setItem('user', JSON.stringify(resolvedUser));
            }
          } catch { }
          applyUserData(resolvedUser);
        }
      } catch (err) {
        console.error('Error loading user profile:', err);
        if (isMounted) {
          setUser((prev) => ({
            name: prev.name || '',
            designation: prev.designation || '',
            department: prev.department || '',
          }));
        }
      }
    };

    loadUser();

    return () => {
      isMounted = false;
    };
  }, []);

  // Generic Fetch Function
  const fetchChecklist = async (scope, stateSetter, params = {}) => {
    stateSetter((prev) => ({ ...prev, loading: true, error: null }));

    // Construct query params
    const queryParams = new URLSearchParams({
      scope,
      page: params.page || 1,
      limit: params.limit || 10,
      search: params.search || '',
      filterFreq: params.filterFreq || 'All',
      month: params.month || 'All'
    });

    try {
      const response = await fetch(getApiUrl(`api/yesno/list?${queryParams.toString()}`), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        stateSetter(prev => ({
          ...prev,
          items: data.data || [],
          total: data.meta?.total || 0,
          page: data.meta?.page || 1,
          loading: false,
        }));
      } else {
        stateSetter(prev => ({ ...prev, loading: false, error: data.message }));
      }
    } catch (err) {
      console.error(`Error fetching ${scope} items:`, err);
      stateSetter(prev => ({ ...prev, loading: false, error: err.message }));
    }
  };

  // Initial Fetch & Tab Change Effects
  useEffect(() => {
    if (activeTab === 'planning') {
      fetchChecklist('planning', setPlanningState, {
        page: planningState.page,
        limit: planningState.limit,
        search: planningState.search,
        filterFreq: planningState.filterFreq
      });
    } else if (activeTab === 'execution') {
      // Fetch both today and past
      fetchChecklist('today', setExecutionState, {
        page: executionState.page,
        limit: executionState.limit,
        search: executionState.search,
        filterFreq: executionState.filterFreq
      });

      fetchChecklist('past', setPastState, {
        page: pastState.page,
        limit: pastState.limit,
        search: pastState.search,
        filterFreq: pastState.filterFreq,
        month: pastState.month
      });
    }
  }, [activeTab]); // Only run on tab change

  // Planning Fetch Effect
  useEffect(() => {
    if (activeTab === 'planning') {
      fetchChecklist('planning', setPlanningState, {
        page: planningState.page,
        limit: planningState.limit,
        search: planningState.search,
        filterFreq: planningState.filterFreq
      });
    }
  }, [planningState.page, planningState.limit, planningState.search, planningState.filterFreq]);

  // Execution Fetch Effect
  useEffect(() => {
    if (activeTab === 'execution') {
      fetchChecklist('today', setExecutionState, {
        page: executionState.page,
        limit: executionState.limit,
        search: executionState.search,
        filterFreq: executionState.filterFreq
      });
    }
  }, [executionState.page, executionState.limit, executionState.search, executionState.filterFreq]);

  // Past Fetch Effect
  useEffect(() => {
    if (activeTab === 'execution') {
      fetchChecklist('past', setPastState, {
        page: pastState.page,
        limit: pastState.limit,
        search: pastState.search,
        filterFreq: pastState.filterFreq,
        month: pastState.month
      });
    }
  }, [pastState.page, pastState.limit, pastState.search, pastState.filterFreq, pastState.month]);

  // Separate effect for past search/filterFreq to avoid double fetching if they are tied to executionState controls?
  // For now, let's assume search inputs are independent or we update both if shared.


  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(getApiUrl('api/departments'), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch departments: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data)) {
          setDepartments(data);
        }
      } catch (deptErr) {
        console.error('Error fetching departments:', deptErr);
      }
    };

    fetchDepartments();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'planning' || tab === 'execution') setActiveTab(tab);
  }, [location.search]);

  // Helper to refresh lists after mutation
  const refreshActiveView = () => {
    if (activeTab === 'planning') {
      fetchChecklist('planning', setPlanningState, {
        page: planningState.page,
        search: planningState.search,
        filterFreq: planningState.filterFreq
      });
    } else {
      fetchChecklist('today', setExecutionState, {
        page: executionState.page,
        search: executionState.search,
        filterFreq: executionState.filterFreq
      });
      fetchChecklist('past', setPastState, {
        page: pastState.page,
        month: pastState.month,
        // search: pastState.search // If we want search to apply to past too
      });
    }
  };

  // Simplified "Past Months" options generator
  // Since we don't have all items client side, we might hardcode last 12 months or fetch available months from backend?
  // For now, let's generate last 12 months dynamically
  const pastMonthOptions = React.useMemo(() => {
    const options = [];
    const date = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(date.getFullYear(), date.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
      options.push({ value, label });
    }
    return [{ value: 'All', label: 'All Months' }, ...options];
  }, []);

  // Aliases for render logic compatibility
  const planningItems = planningState.items;
  const filteredList = executionState.items; // Today's execution items
  const filteredPastList = pastState.items; // Past execution items

  // Restore todayDisplayLabel
  const todayDisplayLabel = formatDateForDisplay(todayStr);

  // Static frequency options since we can't count client-side easily without fetching all
  const frequencyFilters = [
    { label: 'All', color: '#a855f7' },
    { label: 'Daily', color: '#3b82f6' },
    { label: 'Alternate', color: '#6366f1' },
    { label: 'Custom', color: '#14b8a6' },
    { label: 'Weekly', color: '#f97316' },
    { label: 'Monthly', color: '#22c55e' },
    { label: 'Quarterly', color: '#ec4899' },
    { label: 'Yearly', color: '#9ca3af' },
  ];

  const executionColumnSpan = 10 + (showDepartment ? 1 : 0) + (showLocation ? 1 : 0);

  const handleModalSubmit = (createdYesNo) => {
    if (createdYesNo) {
      // This logic needs to be updated to use the state setters for planning/execution
      // For now, it's likely a placeholder or needs to trigger a refresh
      refreshActiveView();
    }
    setShowCreateModal(false);
  };

  const convertTo12HourFormat = (timeInput) => {
    if (!timeInput) return '';
    const trimmed = timeInput.toString().trim();
    if (!trimmed) return '';

    const ampmMatch = trimmed.match(/^([0-1]?\d):([0-5]\d)\s*(AM|PM)$/i);
    if (ampmMatch) {
      const hours = parseInt(ampmMatch[1], 10) % 12 || 12;
      const minutes = ampmMatch[2];
      const period = ampmMatch[3].toUpperCase();
      return `${String(hours).padStart(2, '0')}:${minutes} ${period}`;
    }

    const hhmmMatch = trimmed.match(/^([0-2]?\d):([0-5]\d)$/);
    if (!hhmmMatch) {
      return trimmed;
    }

    const hours24 = parseInt(hhmmMatch[1], 10);
    const minutes = hhmmMatch[2];
    if (Number.isNaN(hours24)) {
      return trimmed;
    }

    const period = hours24 >= 12 ? 'PM' : 'AM';
    const displayHour = hours24 % 12 || 12;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${period}`;
  };

  const convertTo24HourFormat = (timeInput) => {
    if (!timeInput) return null;
    const trimmed = timeInput.toString().trim();
    if (!trimmed) return null;

    const ampmMatch = trimmed.match(/^([0-1]?\d):([0-5]\d)\s*(AM|PM)$/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = ampmMatch[2];
      const period = ampmMatch[3].toUpperCase();
      if (period === 'PM' && hours < 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    const hhmmMatch = trimmed.match(/^([0-2]?\d):([0-5]\d)$/);
    if (hhmmMatch) {
      const hours = parseInt(hhmmMatch[1], 10);
      if (Number.isNaN(hours)) {
        return null;
      }
      return `${String(hours).padStart(2, '0')}:${hhmmMatch[2]}`;
    }

    return null;
  };

  const normalizeToHHMM = (timeInput, fallback = '') => {
    const converted = convertTo24HourFormat(timeInput);
    if (converted) {
      return converted;
    }

    if (!timeInput) {
      return fallback;
    }

    const trimmed = timeInput.toString().trim();
    if (!trimmed) {
      return fallback;
    }

    const match = trimmed.match(/^([0-2]?\d):([0-5]\d)/);
    if (match) {
      const hours = match[1].padStart(2, '0');
      const minutes = match[2];
      return `${hours}:${minutes}`;
    }

    return fallback;
  };

  const normalizeNumberInputValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = value.toString().trim();
    if (!normalized) return '';
    const parsed = parseFloat(normalized.replace(/,/g, ''));
    if (Number.isNaN(parsed)) {
      return '';
    }
    return String(parsed);
  };

  const sanitizeNumberForPayload = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = value.toString().trim();
    if (!normalized) return null;
    const parsed = parseFloat(normalized.replace(/,/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const convertTo24Hour = (time12h) => {
    if (!time12h) return '';

    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (modifier === 'PM' && hours !== '12') {
      hours = String(Number(hours) + 12);
    }
    if (modifier === 'AM' && hours === '12') {
      hours = '00';
    }

    return `${hours.padStart(2, '0')}:${minutes}`;
  };


  const buildPlanningRow = (item) => {
    if (!item) {
      return null;
    }

    const isEditing = editingPlannedId === item.id;
    const rowKey = `plan-${item.id || item.recurrenceGroupId || item.name}-${toLocalDateKey(item.occurrenceDate) || 'base'
      }`;

    return (
      <tr key={rowKey}>
        <td className="yn-dep">
          {(item.department || 'N/A').length > 22
            ? `${(item.department || 'N/A').slice(0, 21)}...`
            : item.department || 'N/A'}
        </td>
        <td className="yn-subject">{item.name}</td>
        <td className="yn-kra">
          {item.kra || (item.description ? `${item.description.slice(0, 15)}...` : '')}
        </td>
        <td className="yn-time-input">
          {isEditing ? (
            <input
              type="time"
              value={convertTo24Hour(editingPlannedTimes.from)}
              onChange={(e) =>
                setEditingPlannedTimes({ ...editingPlannedTimes, from: e.target.value })
              }
            />
          ) : (
            item.plannedFromTime || '--:--'
          )}
        </td>
        <td className="yn-time-input">
          {isEditing ? (
            <input
              type="time"
              value={convertTo24Hour(editingPlannedTimes.to)}
              onChange={(e) =>
                setEditingPlannedTimes({ ...editingPlannedTimes, to: e.target.value })
              }
            />
          ) : (
            item.plannedToTime || '--:--'
          )}
        </td>
        <td className="yn-location">{item.location || 'HEAD OFFICE'}</td>
        <td className="yn-frequency">
          <span className="yn-freq-badge">
            {item.frequency ? item.frequency.charAt(0) : 'D'}
          </span>
        </td>
        <td className="yn-day">
          <span className="yn-day-label">{formatDayDisplay(item)}</span>
        </td>
        <td className="yn-actions">
          {isEditing ? (
            <>
              <button
                className="yn-action-save"
                title="Save"
                onClick={() => handleSavePlanning(item.id)}
              >
                ✓
              </button>
              <button
                className="yn-action-cancel"
                title="Cancel"
                onClick={() => {
                  setEditingPlannedId(null);
                  setEditingPlannedTimes({ from: '', to: '' });
                }}
              >
                ✕
              </button>
            </>
          ) : (
            <>
              <button
                className="yn-action-edit"
                title="Edit Time"
                onClick={() => {
                  setEditingPlannedId(item.id);
                  setEditingPlannedTimes({
                    from: item.plannedFromTime || '--:--',
                    to: item.plannedToTime || '--:--',
                  });
                }}
              >
                ✏️
              </button>
              <button
                className="yn-action-delete"
                title="Delete"
                onClick={() => handleDelete(item.id)}
              >
                🗑️
              </button>
            </>
          )}
        </td>
      </tr>
    );
  };

  const buildExecutionRow = (item) => {
    if (!item) {
      return null;
    }

    const isEditing = editingExecutionId === item.id;
    const rowKey = `exec-${item.id || item.recurrenceGroupId || item.name}`;
    const plannedFromTime = normalizeToHHMM(item.plannedFromTime, '');
    const plannedToTime = normalizeToHHMM(item.plannedToTime, '');
    const actualFromTime = normalizeToHHMM(item.actualFromTime, '');
    const actualToTime = normalizeToHHMM(item.actualToTime, '');

    return (
      <tr key={rowKey}>
        {showDepartment && <td className="yn-exec-department">{item.department || 'N/A'}</td>}
        {showLocation && <td className="yn-exec-location">{item.location || 'HEAD OFFICE'}</td>}
        <td className="yn-exec-planned-subject">{item.name}</td>
        <td className="yn-exec-actual-subject">
          {isEditing ? (
            <input
              type="text"
              placeholder="Actual Subject"
              value={editingExecutionData.actualSubject}
              onChange={(e) =>
                setEditingExecutionData({
                  ...editingExecutionData,
                  actualSubject: e.target.value,
                })
              }
            />
          ) : (
            <span>{item.actualSubject || '--'}</span>
          )}
        </td>
        <td className="yn-exec-frequency">
          <span className="yn-freq-badge-exec">
            {item.frequency ? item.frequency.charAt(0) : 'D'}
          </span>
        </td>
        <td className="yn-exec-time-input">
          <input
            type="text"
            placeholder="--:--"
            defaultValue={plannedFromTime}
            disabled
          />
        </td>
        <td className="yn-exec-time-input">
          <input
            type="text"
            placeholder="--:--"
            defaultValue={plannedToTime}
            disabled
          />
        </td>
        <td className="yn-exec-actual-input">
          {isEditing ? (
            <input
              type="time"
              value={editingExecutionData.actualFromTime || ''}
              onChange={(e) =>
                setEditingExecutionData({
                  ...editingExecutionData,
                  actualFromTime: e.target.value,
                })
              }
            />
          ) : (
            <span>{actualFromTime || '--:--'}</span>
          )}
        </td>
        <td className="yn-exec-actual-input">
          {isEditing ? (
            <input
              type="time"
              value={editingExecutionData.actualToTime || ''}
              onChange={(e) =>
                setEditingExecutionData({
                  ...editingExecutionData,
                  actualToTime: e.target.value,
                })
              }
            />
          ) : (
            <span>{actualToTime || '--:--'}</span>
          )}
        </td>
        <td className="yn-exec-marks">
          {isEditing ? (
            <input
              type="text"
              placeholder="Remarks"
              value={editingExecutionData.marks}
              onChange={(e) =>
                setEditingExecutionData({
                  ...editingExecutionData,
                  marks: e.target.value,
                })
              }
            />
          ) : (
            item.marks || '-'
          )}
        </td>
        <td className="yn-exec-action">
          {isEditing ? (
            <>
              <button
                className="yn-yes-btn"
                onClick={() => {
                  setRemarksData({
                    remarks: '',
                    yesNoId: item.id,
                    yesStatus: true,
                    noStatus: false,
                  });
                  setShowRemarksModal(true);
                }}
                style={{ backgroundColor: editingExecutionData.yesStatus ? '#4CAF50' : '#ccc' }}
              >
                Yes
              </button>
              <button
                className="yn-no-btn"
                onClick={() => {
                  setRemarksData({
                    remarks: '',
                    yesNoId: item.id,
                    yesStatus: false,
                    noStatus: true,
                  });
                  setShowRemarksModal(true);
                }}
                style={{ backgroundColor: editingExecutionData.noStatus ? '#f44336' : '#ccc' }}
              >
                No
              </button>
              <button
                className="yn-view-remarks-btn"
                onClick={() => setViewRemarksData(item)}
                title="View Details"
              >
                👁️
              </button>
            </>
          ) : (
            <>
              <span>
                {item.yesStatus ? '✓ Yes' : item.noStatus ? '✗ No' : '-'}
              </span>
              {(item.yesStatus || item.noStatus) && (
                <button
                  className="yn-view-remarks-btn"
                  onClick={() => setViewRemarksData(item)}
                  title="View Remarks"
                >
                  👁️
                </button>
              )}
            </>
          )}
        </td>
        <td className="yn-exec-arrow">
          {isEditing ? (
            <>
              <button onClick={() => handleSaveExecution(item.id)} title="Save">
                ✓
              </button>
              <button
                onClick={() => {
                  setEditingExecutionId(null);
                  setEditingExecutionData({
                    actualSubject: '',
                    actualFromTime: '',
                    actualToTime: '',
                    plannedQty: '',
                    actualQty: '',
                    marks: '',
                    yesStatus: false,
                    noStatus: false,
                  });
                }}
                title="Cancel"
              >
                ✕
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                setEditingExecutionId(item.id);
                setEditingExecutionData({
                  actualSubject: item.actualSubject || '',
                  actualFromTime: normalizeToHHMM(item.actualFromTime, '') || '',
                  actualToTime: normalizeToHHMM(item.actualToTime, '') || '',
                  plannedQty: normalizeNumberInputValue(item.plannedQty),
                  actualQty: normalizeNumberInputValue(item.actualQty),
                  marks: item.marks ? item.marks.toString() : '',
                  yesStatus: Boolean(item.yesStatus),
                  noStatus: Boolean(item.noStatus),
                });
              }}
              title="Edit"
            >
              ✏️
            </button>
          )}
        </td>
      </tr>
    );
  };

  // Calculate time difference in minutes
  const calculateTimeDifference = (fromTime, toTime) => {
    if (!fromTime || !toTime) return 0;

    try {
      // If times are in 12-hour format (with AM/PM), convert to 24-hour
      let from24 = fromTime;
      let to24 = toTime;

      if (fromTime.includes('AM') || fromTime.includes('PM')) {
        from24 = convertTo24HourFormat(fromTime);
      }
      if (toTime.includes('AM') || toTime.includes('PM')) {
        to24 = convertTo24HourFormat(toTime);
      }

      if (!from24 || !to24) return 0;

      // Parse time in HH:MM format (24-hour)
      const [fromHours, fromMinutes] = from24.split(':').map(Number);
      const [toHours, toMinutes] = to24.split(':').map(Number);

      if (isNaN(fromHours) || isNaN(fromMinutes) || isNaN(toHours) || isNaN(toMinutes)) {
        return 0;
      }
      // Handle case where end time is on next day
      const fromTotalMinutes = fromHours * 60 + fromMinutes;
      const toTotalMinutes = toHours * 60 + toMinutes;
      let difference = toTotalMinutes - fromTotalMinutes;
      if (difference < 0) {
        difference += 24 * 60; // Add 24 hours
      }
      return difference;
    } catch (err) {
      return 0;
    }
  };

  // Convert minutes to hours and minutes format
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Calculate total planned hours for current date
  const calculateTotalPlannedHours = () => {
    return filteredList.reduce((total, item) => {
      const diff = calculateTimeDifference(item.plannedFromTime, item.plannedToTime);
      return total + diff;
    }, 0);
  };

  // Calculate total actual hours for current date
  const calculateTotalActualHours = () => {
    return filteredList.reduce((total, item) => {
      if (item.actualFromTime && item.actualToTime) {
        const diff = calculateTimeDifference(item.actualFromTime, item.actualToTime);
        return total + diff;
      }
      return total;
    }, 0);
  };

  // Planning: Save planned times
  // Convert 24-hour time (HH:MM) to 12-hour time (HH:MM AM/PM)
  const handleSavePlanning = async (id) => {
    if (!editingPlannedTimes.from || !editingPlannedTimes.to) {
      alert('Please enter both planned times');
      return;
    }

    try {
      // Convert time format from 24-hour to 12-hour AM/PM format
      const response = await fetch(getApiUrl(`api/yesno/${id}/save-planning`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plannedFromTime: convertTo12HourFormat(editingPlannedTimes.from),
          plannedToTime: convertTo12HourFormat(editingPlannedTimes.to),
        }),
      });

      const data = await response.json();
      if (data.success) {
        setPlanningState((prev) => ({
          ...prev,
          items: prev.items.map((item) => (item.id === id ? data.data : item))
        }));
        setEditingPlannedId(null);
        setEditingPlannedTimes({ from: '', to: '' });
        alert('Planned time saved successfully!');
      } else {
        alert(data.message || 'Failed to save planned time');
      }
    } catch (error) {
      console.error('Error saving planning:', error);
      alert('Error saving planned time');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Delete this Daily Checklist item? This cannot be undone.');
    if (!confirmed) return;

    try {
      const response = await fetch(getApiUrl(`api/yesno/${id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || `Failed with status ${response.status}`);
      }

      const deletedGroupId = data.recurrenceGroupId;
      setPlanningState((prev) => ({
        ...prev,
        items: prev.items.filter((item) =>
          deletedGroupId
            ? item.recurrenceGroupId !== deletedGroupId
            : item.id !== id
        )
      }));
      if (editingPlannedId === id) {
        setEditingPlannedId(null);
        setEditingPlannedTimes({ from: '', to: '' });
      }
      if (editingExecutionId === id) {
        setEditingExecutionId(null);
        setEditingExecutionData({
          actualSubject: '',
          actualFromTime: '',
          actualToTime: '',
          plannedQty: '',
          actualQty: '',
          marks: '',
          yesStatus: false,
          noStatus: false,
        });
      }

      alert('Daily Checklist deleted successfully.');
    } catch (err) {
      console.error('Error deleting Daily Checklist item:', err);
      alert(err.message || 'Failed to delete Daily Checklist item');
    }
  };

  // Execution: Save execution data
  const handleSaveExecution = async (id) => {
    try {
      const toNullableString = (value) => {
        if (value === null || value === undefined) return null;
        const trimmed = value.toString().trim();
        return trimmed.length ? trimmed : null;
      };

      const formattedFromTime = convertTo12HourFormat(editingExecutionData.actualFromTime);
      const formattedToTime = convertTo12HourFormat(editingExecutionData.actualToTime);

      const executionDataToSend = {
        actualSubject: toNullableString(editingExecutionData.actualSubject),
        actualFromTime: formattedFromTime || null,
        actualToTime: formattedToTime || null,
        plannedQty: sanitizeNumberForPayload(editingExecutionData.plannedQty),
        actualQty: sanitizeNumberForPayload(editingExecutionData.actualQty),
        marks: toNullableString(editingExecutionData.marks),
        yesStatus: Boolean(editingExecutionData.yesStatus),
        noStatus: Boolean(editingExecutionData.noStatus),
      };

      const response = await fetch(getApiUrl(`api/yesno/${id}/save-execution`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(executionDataToSend),
      });

      const data = await response.json();
      if (data.success) {
        // Update current item
        setExecutionState((prev) => ({
          ...prev,
          items: prev.items.map((item) => (item.id === id ? data.data : item))
        }));

        // If next occurrence was created, add it to the list
        if (data.nextOccurrence && data.nextOccurrence.success) {
          setExecutionState((prev) => ({
            ...prev,
            items: [data.nextOccurrence.data, ...prev.items]
          }));
        }

        setEditingExecutionId(null);
        setEditingExecutionData({
          actualSubject: '',
          actualFromTime: '',
          actualToTime: '',
          plannedQty: '',
          actualQty: '',
          marks: '',
          yesStatus: false,
          noStatus: false,
        });

        posthog.capture('Daily Checklist Executed', executionDataToSend);

        alert('Execution saved successfully!');
      } else {
        alert(data.message || 'Failed to save execution');
      }
    } catch (error) {
      console.error('Error saving execution:', error);
      alert('Error saving execution');
    }
  };

  // Handle export data to Excel
  const handleExportData = async () => {
    try {
      const response = await fetch(getApiUrl('api/yesno/export/data'), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `DailyChecklist_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  const timeToMinutes = (time) => {
    if (!time) return Number.MAX_SAFE_INTEGER;

    // 24-hour format: "08:20"
    if (/^\d{2}:\d{2}$/.test(time)) {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    }

    // AM/PM format: "08:20 AM"
    const match = time.match(/(\d{1,2}):(\d{2})\s?(AM|PM)/i);
    if (!match) return Number.MAX_SAFE_INTEGER;

    let [, h, m, meridian] = match;
    h = Number(h);
    m = Number(m);

    if (meridian.toUpperCase() === "PM" && h !== 12) h += 12;
    if (meridian.toUpperCase() === "AM" && h === 12) h = 0;

    return h * 60 + m;
  };


  return (
    <DashboardLayout>
      {activeTab === 'planning' ? (
        /* PLANNING VIEW */
        <div className="yesno-planning-container">
          {/* Header */}
          <div className="yesno-planning-header">
            <div className="yesno-planning-header-left">
              <h1 className="yesno-planning-title">
                My Daily Checklist
              </h1>
              {/* Date Header */}
              <div className="yesno-date-header">
                <span className="yesno-date-label" style={{ fontSize: '12px' }}>Date: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              </div>
            </div>
            <div className="yesno-planning-actions">
              <button className="yesno-btn-bulk" onClick={() => setShowBulkUploadModal(true)}>Bulk Upload</button>
              <button
                className="yesno-btn-create"
                onClick={() => setShowCreateModal(true)}
              >
                Create Daily Checklist
              </button>
              <button className="yesno-btn-export" onClick={handleExportData}>📊 Export Data</button>
            </div>
          </div>

          {/* Frequency Filter Pills */}
          <div className="yesno-planning-filters">
            {frequencyFilters.map((filter) => (
              <button
                key={filter.label}
                className={`yn-freq-pill ${planningState.filterFreq === filter.label ? 'active' : ''}`}
                style={{
                  backgroundColor:
                    planningState.filterFreq === filter.label ? filter.color : '#f0f0f0',
                  color: planningState.filterFreq === filter.label ? '#fff' : '#333',
                }}
                onClick={() => setPlanningState(prev => ({ ...prev, filterFreq: filter.label, page: 1 }))}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="yesno-planning-search">
            <input
              type="text"
              placeholder="Search:"
              value={planningState.search}
              onChange={(e) => setPlanningState(prev => ({ ...prev, search: e.target.value, page: 1 }))}
              className="yn-search-input"
            />
          </div>

          {/* Table */}
          <div className="yesno-planning-table-wrapper">
            <table className="yesno-planning-table">
              <thead>
                <tr>
                  <th>Dep.</th>
                  <th>Subject</th>
                  <th>KRA</th>
                  <th colSpan="2" className="yn-time-header">
                    Planned
                  </th>
                  <th>Location</th>
                  <th>Frequency</th>
                  <th>Day</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {planningState.loading ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: "center", padding: "24px" }}>
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                        <div className="table-loader" />
                        <span>Loading.....</span>
                      </div>
                    </td>
                  </tr>
                ) : planningItems.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="yn-no-data">
                      No data available
                    </td>
                  </tr>
                ) : (
                  planningItems
                    .sort((a, b) => {
                      return timeToMinutes(a.plannedFromTime) - timeToMinutes(b.plannedFromTime);
                    }).map((item) => buildPlanningRow(item))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="yesno-planning-pagination">
            <Pagination
              currentPage={planningState.page}
              totalPages={Math.ceil(planningState.total / planningState.limit)}
              totalItems={planningState.total}
              onPageChange={(page) => setPlanningState(prev => ({ ...prev, page }))}
              pageSize={planningState.limit}
              onPageSizeChange={(limit) => setPlanningState(prev => ({ ...prev, limit, page: 1 }))}
            />
          </div>

          {/* Modal */}
          <CreateYesNoModal
            open={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleModalSubmit}
            departments={departments}
          />
        </div>
      ) : (
        /* EXECUTION VIEW */
        <div className="yesno-execution-container">
          {/* Header */}
          <div className="yesno-execution-header">
            <div className="yesno-execution-header-left">
              <h1 className="yesno-execution-title">
                Execution
              </h1>
              {user.designation ? (
                <p className="yesno-execution-designation">{user.designation}</p>
              ) : null}
            </div>
            <div className="yesno-exec-controls">
              <div className="yn-exec-search">
                <div className="yn-search-container" style={{ display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}>
                  <input
                    type="text"
                    placeholder="Search execution..."
                    value={executionState.localSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setExecutionState(prev => ({ ...prev, localSearch: val }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setExecutionState(prev => ({ ...prev, search: prev.localSearch, page: 1 }));
                        setPastState(prev => ({ ...prev, search: executionState.localSearch, page: 1 }));
                      }
                    }}
                    className="yn-search-input"
                  />
                  <button
                    className="yn-search-btn"
                    onClick={() => {
                      setExecutionState(prev => ({ ...prev, search: prev.localSearch, page: 1 }));
                      setPastState(prev => ({ ...prev, search: executionState.localSearch, page: 1 }));
                    }}
                    title="Click to search"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                    }}
                  >
                    🔍
                  </button>
                  {executionState.localSearch && (
                    <button
                      className="yn-search-btn yn-search-reset"
                      onClick={() => {
                        setExecutionState(prev => ({ ...prev, search: '', localSearch: '', page: 1 }));
                        setPastState(prev => ({ ...prev, search: '', page: 1 }));
                      }}
                      title="Reset Search"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        color: '#ef4444',
                      }}
                    >
                      ✖
                    </button>
                  )}
                </div>
              </div>
              <div className="yn-exec-toggles">
                <div className="yn-toggle-group">
                  <span className="yn-toggle-label">Department</span>
                  <label className="yn-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showDepartment}
                      onChange={(e) => setShowDepartment(e.target.checked)}
                    />
                    <span className="yn-toggle-slider"></span>
                  </label>
                </div>
                <div className="yn-toggle-group">
                  <span className="yn-toggle-label">Location</span>
                  <label className="yn-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showLocation}
                      onChange={(e) => setShowLocation(e.target.checked)}
                    />
                    <span className="yn-toggle-slider"></span>
                  </label>
                </div>
              </div>
              <button
                className="yn-exec-share-btn"
                onClick={handleExportData}
                title="Export current data"
              >
                ⬇ Export
              </button>
            </div>
          </div>

          {/* Current Date Summary */}
          <div
            className={`yn-collapsible-header ${isTodayExpanded ? 'expanded' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => setIsTodayExpanded((prev) => !prev)}
            onKeyDown={(event) => handleToggleKeyPress(event, setIsTodayExpanded)}
            aria-expanded={isTodayExpanded}
            aria-controls="yn-today-section"
          >
            <div className="yn-date-info">
              <span className="yn-date">{todayDisplayLabel}</span>
              <span className="yn-count">({executionState.total})</span>
              <span className="yn-planned-hrs">
                | Total Planned Hrs: {formatDuration(calculateTotalPlannedHours())} (approx) |
              </span>
              <span className="yn-actual-hrs">
                Total Actual Hrs: {formatDuration(calculateTotalActualHours())} (approx)
              </span>
            </div>
            <span className="yn-collapse-icon" aria-hidden="true">&gt;</span>
          </div>

          {/* Current Date Execution Table */}
          {isTodayExpanded && (
            <div className="yn-exec-section" id="yn-today-section">
              <div className="yn-exec-table-wrapper">
                <table className="yn-exec-table">
                  <thead>
                    <tr>
                      {showDepartment && <th>Department</th>}
                      {showLocation && <th>Location</th>}
                      <th>Planned Subject</th>
                      <th>Actual Subject</th>
                      <th>Frequency</th>
                      <th colSpan="2">Planned Time</th>
                      <th colSpan="2">Actual Time</th>
                      <th>Remarks</th>
                      <th>Action</th>
                    </tr>
                    <tr>
                      {showDepartment && <th></th>}
                      {showLocation && <th></th>}
                      <th></th>
                      <th></th>
                      <th></th>
                      <th>From time</th>
                      <th>To time</th>
                      <th>From time</th>
                      <th>To time</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {executionState.loading ? (
                      <tr>
                        <td colSpan="10" style={{ textAlign: "center", padding: "24px" }}>
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                            <div className="table-loader" />
                            <span>Loading....</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredList.length === 0 ? (
                      <tr>
                        <td colSpan={executionColumnSpan} className="yn-no-data">No data available</td>
                      </tr>
                    ) : (
                      filteredList.map((item) => buildExecutionRow(item))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="yesno-pagination-wrapper">
                <Pagination
                  currentPage={executionState.page}
                  totalPages={Math.ceil(executionState.total / executionState.limit)}
                  totalItems={executionState.total}
                  onPageChange={(page) => setExecutionState(prev => ({ ...prev, page }))}
                  pageSize={executionState.limit}
                  onPageSizeChange={(limit) => setExecutionState(prev => ({ ...prev, limit, page: 1 }))}
                />
              </div>
            </div>
          )}



          {(
            <>
              <div
                className={`yn-collapsible-header yn-collapsible-header--past ${isPastExpanded ? 'expanded' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setIsPastExpanded((prev) => !prev)}
                onKeyDown={(event) => handleToggleKeyPress(event, setIsPastExpanded)}
                aria-expanded={isPastExpanded}
                aria-controls="yn-past-section"
              >
                <div className="yn-date-info">
                  <span className="yn-date">Past Tasks</span>
                  <span className="yn-count">({pastState.total})</span>
                  <span className="yn-last-days-note">*showing all past tasks</span>
                  <div className="yn-past-filter" onClick={(e) => e.stopPropagation()}>
                    <label htmlFor="yn-past-month-filter">Month:</label>
                    <select
                      id="yn-past-month-filter"
                      value={pastState.month}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(event) => { event.stopPropagation(); setPastState(prev => ({ ...prev, month: event.target.value, page: 1 })); }}
                    >
                      {pastMonthOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <span className="yn-collapse-icon" aria-hidden="true">&gt;</span>
              </div>
              {isPastExpanded && (
                <div className="yn-exec-section" id="yn-past-section">
                  <div className="yn-exec-table-wrapper">
                    <table className="yn-exec-table">
                      <thead>
                        <tr>
                          {showDepartment && <th>Department</th>}
                          {showLocation && <th>Location</th>}
                          <th>Planned Subject</th>
                          <th>Date</th>
                          <th>Actual Subject</th>
                          <th>Frequency</th>
                          <th colSpan="2">Planned Time</th>
                          <th colSpan="2">Actual Time</th>
                          <th>Marks</th>
                          <th>Action</th>
                        </tr>
                        <tr>
                          {showDepartment && <th></th>}
                          {showLocation && <th></th>}
                          <th></th>
                          <th></th>
                          <th></th>
                          <th></th>
                          <th>From time</th>
                          <th>To time</th>
                          <th>From time</th>
                          <th>To time</th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pastState.loading ? (
                          <tr>
                            <td colSpan="12" style={{ textAlign: "center", padding: "24px" }}>
                              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                <div className="table-loader" />
                                <span>Loading....</span>
                              </div>
                            </td>
                          </tr>
                        ) : filteredPastList.length === 0 ? (
                          <tr>
                            <td colSpan="12" className="yn-no-data">No history available</td>
                          </tr>
                        ) : (
                          filteredPastList.map((item, idx) => {
                            const plannedFromTime = normalizeToHHMM(item.plannedFromTime, '');
                            const plannedToTime = normalizeToHHMM(item.plannedToTime, '');
                            const actualFromTime = normalizeToHHMM(item.actualFromTime, '');
                            const actualToTime = normalizeToHHMM(item.actualToTime, '');

                            return (
                              <tr key={idx}>
                                {showDepartment && <td className="yn-exec-department">{item.department || 'N/A'}</td>}
                                {showLocation && <td className="yn-exec-location">{item.location || 'HEAD OFFICE'}</td>}
                                <td className="yn-exec-planned-subject">{item.name}</td>
                                <td className="yn-exec-date">{getOccurrenceDisplayDate(item)}</td>
                                <td className="yn-exec-actual-subject">
                                  {editingExecutionId === item.id ? (
                                    <input
                                      type="text"
                                      placeholder="Subject"
                                      value={editingExecutionData.actualSubject}
                                      onChange={(e) =>
                                        setEditingExecutionData({
                                          ...editingExecutionData,
                                          actualSubject: e.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    item.actualSubject || '-'
                                  )}
                                </td>
                                <td className="yn-exec-frequency">
                                  <span className="yn-freq-badge">{item.frequency ? item.frequency.charAt(0) : 'D'}</span>
                                </td>
                                <td className="yn-exec-time-input">
                                  <input
                                    type="text"
                                    placeholder="--:--"
                                    defaultValue={plannedFromTime}
                                    disabled
                                  />
                                </td>
                                <td className="yn-exec-time-input">
                                  <input
                                    type="text"
                                    placeholder="--:--"
                                    defaultValue={plannedToTime}
                                    disabled
                                  />
                                </td>
                                <td className="yn-exec-actual-input">
                                  <span>{actualFromTime || plannedFromTime}</span>
                                </td>
                                <td className="yn-exec-actual-input">
                                  <span>{actualToTime || plannedToTime}</span>
                                </td>
                                <td className="yn-exec-marks">{item.marks || '-'}</td>
                                <td className="yn-exec-action">
                                  <span>{item.yesStatus ? '✓ Yes' : item.noStatus ? '✗ No' : '-'}</span>
                                  {(item.yesStatus || item.noStatus) && (
                                    <button
                                      className="yn-view-remarks-btn"
                                      onClick={() => setViewRemarksData(item)}
                                      title="View Remarks"
                                    >
                                      👁️
                                    </button>
                                  )}
                                </td>
                                <td className="yn-exec-arrow">
                                  <button
                                    onClick={() => {
                                      setEditingExecutionId(item.id);
                                      setEditingExecutionData({
                                        actualSubject: item.actualSubject || '',
                                        actualFromTime: normalizeToHHMM(item.actualFromTime, plannedFromTime) || plannedFromTime,
                                        actualToTime: normalizeToHHMM(item.actualToTime, plannedToTime) || plannedToTime,
                                        plannedQty: normalizeNumberInputValue(item.plannedQty),
                                        actualQty: normalizeNumberInputValue(item.actualQty),
                                        marks: item.marks ? item.marks.toString() : '',
                                        yesStatus: Boolean(item.yesStatus),
                                        noStatus: Boolean(item.noStatus),
                                      });
                                    }}
                                    title="Edit"
                                  >
                                    ✏️
                                  </button>
                                </td>
                              </tr>
                            );
                          }))}
                      </tbody>
                    </table>
                  </div>
                  <div className="yesno-pagination-wrapper">
                    <Pagination
                      currentPage={pastState.page}
                      totalPages={Math.ceil(pastState.total / pastState.limit)}
                      totalItems={pastState.total}
                      onPageChange={(page) => setPastState(prev => ({ ...prev, page }))}
                      pageSize={pastState.limit}
                      onPageSizeChange={(limit) => setPastState(prev => ({ ...prev, limit, page: 1 }))}
                    />
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      )}

      {/* Modals - Outside of planning/execution conditional so they're always accessible */}
      {/* Modal */}
      <CreateYesNoModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleModalSubmit}
        departments={departments}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadYesNoModal
        open={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onBulkUploadSuccess={() => refreshActiveView()}
      />

      {/* Remarks Modal */}
      {showRemarksModal && (
        <div className="yn-remarks-overlay" onClick={() => setShowRemarksModal(false)}>
          <div className="yn-remarks-modal" onClick={(e) => e.stopPropagation()}>
            <div className="yn-remarks-header">
              <h3>[{remarksData.yesStatus ? 'YES' : 'NO'}] - Add Remarks</h3>
              <button className="yn-remarks-close" onClick={() => setShowRemarksModal(false)}>✕</button>
            </div>
            <div className="yn-remarks-body">
              <div className="yn-remarks-field">
                <label>Remarks</label>
                <textarea
                  className="yn-remarks-textarea"
                  placeholder="Add Remarks"
                  value={remarksData.remarks}
                  onChange={(e) => setRemarksData({ ...remarksData, remarks: e.target.value })}
                  rows="6"
                />
                <div className="yn-remarks-counter">Characters entered: {remarksData.remarks.length}</div>
              </div>
            </div>
            <div className="yn-remarks-footer">
              <button
                className="yn-remarks-cancel"
                onClick={() => setShowRemarksModal(false)}
              >
                Cancel
              </button>
              <button
                className="yn-remarks-submit"
                onClick={() => {
                  // Update the execution data with remarks
                  setEditingExecutionData({
                    ...editingExecutionData,
                    yesStatus: remarksData.yesStatus,
                    noStatus: remarksData.noStatus,
                    marks: remarksData.remarks,
                  });
                  setShowRemarksModal(false);
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Remarks Modal */}
      {viewRemarksData && (
        <div className="yn-remarks-overlay" onClick={() => setViewRemarksData(null)}>
          <div className="yn-remarks-modal yn-view-remarks-modal" onClick={(e) => e.stopPropagation()}>
            <div className="yn-remarks-header">
              <h3>[{viewRemarksData.yesStatus ? 'YES' : 'NO'}] - View Details</h3>
              <button className="yn-remarks-close" onClick={() => setViewRemarksData(null)}>✕</button>
            </div>
            <div className="yn-remarks-body">
              <div className="yn-view-field">
                <label>Subject:</label>
                <span>{viewRemarksData.actualSubject || '-'}</span>
              </div>
              <div className="yn-view-field">
                <label>Actual Time:</label>
                <span>{viewRemarksData.actualFromTime || '--:--'} - {viewRemarksData.actualToTime || '--:--'}</span>
              </div>
              <div className="yn-view-field">
                <label>Quantity:</label>
                <span>Planned: {viewRemarksData.plannedQty || '-'} | Actual: {viewRemarksData.actualQty || '-'} | Diff: {viewRemarksData.qtyDifference || '-'}</span>
              </div>
              <div className="yn-view-field">
                <label>Remarks:</label>
                <div className="yn-remarks-display">{viewRemarksData.marks || 'No remarks'}</div>
              </div>
            </div>
            <div className="yn-remarks-footer">
              <button
                className="yn-remarks-cancel"
                onClick={() => setViewRemarksData(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default YesNo;
